import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import {
  buildFrameDocument,
  FALLBACK_FRAME_HEIGHT,
  SILENT_FRAME_MS,
  estimateFrameHeight,
  noteFrameWidth,
  fitFrame,
  frameCanvas,
  FRAME_SANDBOX,
  FRAME_SANDBOX_TOKENS,
  frameLinkURL,
  MAX_FRAME_HEIGHT,
  MIN_FRAME_HEIGHT,
  fragmentPosition,
  fragmentTargetID,
  openableURL,
  scrollFrameTo,
  prepareFrameLinks,
  selectedLinkURLs,
  type FrameAppearance,
  type FrameFit,
  type FrameFitResult,
} from './frameDocument';
import { adoptStylesheet, applyDarkAdaptation } from './darkAdapt';
import { reportClientError } from '@/errorReporting/reporter';
import './message-frame.css';
import { IsolatedMessageFrame, type FrameIsolation } from './IsolatedMessageFrame';
import { isLegacyForced } from './isolatedFrame';
import { LinkPreview } from './LinkPreview';
import { confirmMessage, linkTextOf, linkWarnings } from './linkRisk';
import { useFrameFocusRing } from './frameFocus';
import {
  defaultSubresourceFetcher,
  hasSubresources,
  ObjectUrlRegistry,
  resolveFrameSubresources,
  type SubresourceFetcher,
} from './subresources';

/**
 * The only way sanitized message HTML reaches the DOM (ADR 0017 §7).
 *
 * `body_html` is never passed to `dangerouslySetInnerHTML`: that would put
 * sender markup in the same document as the app chrome, sharing our CSS
 * cascade, and would make any future sanitizer bug a direct account
 * compromise. It goes into a frame that cannot execute anything instead.
 *
 * Three things hold this together, and they are independent:
 *
 *  1. **The sandbox grants no script-execution token.** Nothing in the frame
 *     runs. This is structural, not a filter, so it holds even if the server
 *     pass were bypassed entirely. `allow-same-origin` is granted so this
 *     component can read `contentDocument` to size the frame, prepare the
 *     links and attach the link handler; `allow-popups` and its escape let a
 *     link open a new window and nothing more (see `FRAME_SANDBOX_TOKENS`).
 *  2. **A meta CSP of `default-src 'none'`** as the first child of `<head>`,
 *     with `script-src 'none'` deliberately restating (1).
 *  3. **Every click is `preventDefault`ed in the parent realm** and confirmed
 *     before anything opens. Without this a sandboxed frame will happily
 *     navigate *itself* to a third-party URL on click, which is the whole
 *     tracking-and-phishing path back.
 *
 *     That holds where the engine runs the handler, which is Chromium. WebKit
 *     runs no event listener on a document in a frame without script, even
 *     one attached from this realm (ADR 0052), so there the handler never
 *     fires, the frame's own navigation is refused by the sandbox and the app
 *     shell's `frame-src 'none'`, and a link did nothing at all. For that case
 *     the parent also marks each link it would open with `target="_blank"`
 *     and `rel="noopener noreferrer"` (`prepareFrameLinks`), so the engine
 *     opens exactly the validated URL in a new window, without the confirm.
 *
 * Since ADR 0041 there is a fourth, and it closes the last hole in the other
 * three: **the frame fetches nothing at all**. Inline `cid:` parts and sealed
 * remote refs are fetched by this component with a session proof and injected
 * as `blob:` URLs, and the CSP grants `img-src blob:` and nothing else. Before
 * that, `img-src 'self'` meant a sanitizer bypass could still make the browser
 * issue a cookie-authenticated GET against our own API from inside the frame.
 * It cannot now: a `blob:` URL does not reach the network.
 *
 * Everything read out of the frame is data, never instructions: heights are
 * numbers and are clamped; an href is re-parsed and re-validated in this realm
 * before it is used.
 */
export interface MessageFrameProps {
  /** Sanitized HTML from `MessageDetail.bodyHtml`. Never anything else. */
  html: string;
  /**
   * The sanitized, scoped stylesheet from `MessageDetail.bodyCss` (ADR 0081).
   * Never anything else: it is adopted into the frame as a stylesheet.
   */
  css?: string | undefined;
  /**
   * How the frame is coloured (ADR 0081): the sender's colours on white, the
   * sender's own dark design, or an adaptation of the light one. Changing it
   * rebuilds the document; it never refetches its images.
   */
  appearance?: FrameAppearance;
  /** Changes force a fresh document and a fresh fetch of its subresources. */
  frameKey: string;
  /**
   * Which message this is. Defaults to `frameKey`. When `frameKey` changes but
   * `messageId` does not -- the reader loaded images, or retried them -- the
   * iframe element and the document it is showing are kept until the new body
   * is resolved, instead of the whole message going blank while it fetches.
   */
  messageId?: string;
  /** Called from the parent realm after the user confirmed; the default opens a new tab. */
  onOpenLink?: (url: URL) => void;
  /**
   * A `mailto:` link was activated. Handed the raw `href`; the caller parses
   * it (it is sender input) and opens its own composer. Absent, a `mailto:`
   * link does nothing, as before.
   */
  onMailto?: (href: string) => void;
  /** Reported after measurement so the pane can reserve space; optional. */
  onHeight?: (px: number) => void;
  /**
   * How a subresource is fetched. Defaults to the shared transport, which is
   * where `X-Session-Proof` is attached; overridden only by tests.
   *
   * Pass a stable reference: it is a dependency of the effect that fetches, so
   * a fresh inline arrow on every render would refetch the whole body.
   */
  fetchSubresource?: SubresourceFetcher;
  /**
   * Reported once per body, after every subresource has settled. `failed > 0`
   * is *not* the same fact as a blocked remote image and the caller must not
   * present it as one.
   */
  onSubresources?: (status: {
    requested: number;
    failed: number;
    refused: number;
    /** SVGs the reader has not allowed; not counted in `refused`. */
    svgBlocked: number;
  }) => void;
  /**
   * How a body wider than the frame is shown (plan/15 §7.3): scaled down to
   * fit (the default), or at actual size with a sideways scroll inside the
   * frame. Changing it re-measures the live document; it never rebuilds it.
   */
  fit?: FrameFit;
  /**
   * Reported after every measurement. `wide` says the body is wider than the
   * frame at actual size, which is when a fit/actual choice means anything.
   */
  onFit?: (status: FrameFitResult & { wide: boolean }) => void;
  /**
   * The user-content origin to frame this body from (ADR 0078), or absent for
   * the srcdoc renderer. `IsolatedMessageFrame` holds everything that differs;
   * the srcdoc renderer below is unchanged by its existence.
   */
  isolation?: FrameIsolation | undefined;
  /** The text part, for the plain-text fallback when an isolated render cannot be made. */
  bodyText?: string | undefined;
}

interface ResolvedBody {
  /**
   * What actually goes into `srcdoc`. Empty while a new message's
   * subresources are in flight; the previous document while the same
   * message's are (see the reset below).
   */
  html: string;
  /** The stylesheet, its background images resolved the same way. */
  css: string;
  pending: boolean;
  requested: number;
  failed: number;
  refused: number;
  svgBlocked: number;
}

/**
 * The synchronous half of resolution.
 *
 * A body with no `<img>` in it needs no fetch and no re-serialization, so it
 * goes into the frame on the first paint, byte for byte as the sanitizer
 * emitted it. Only a body that has images pays for a second render.
 */
function initialBody(html: string, css: string): ResolvedBody {
  return hasSubresources(html, css)
    ? { html: '', css: '', pending: true, requested: 0, failed: 0, refused: 0, svgBlocked: 0 }
    : { html, css, pending: false, requested: 0, failed: 0, refused: 0, svgBlocked: 0 };
}

function defaultOpenLink(url: URL): void {
  // `noopener` is what keeps the opened page from reaching back through
  // `window.opener`; `noreferrer` also suppresses the Referer, which would
  // otherwise carry the account id and message id in the path.
  window.open(url.href, '_blank', 'noopener,noreferrer');
}

/**
 * Throws unless the frame's sandbox is exactly `FRAME_SANDBOX_TOKENS`.
 *
 * This is the runtime half of the tripwire (plan/11 §8.1.4). It runs before
 * `srcdoc` is assigned, so a frame whose sandbox drifted never receives
 * content at all: the failure mode is a blank message, not a silently
 * script-enabled one.
 */
function assertSandboxIsExactlyTheGrant(frame: HTMLIFrameElement): void {
  const attr = frame.getAttribute('sandbox');
  const tokens = (attr ?? '').split(/\s+/).filter(Boolean);
  const ok =
    tokens.length === FRAME_SANDBOX_TOKENS.length &&
    tokens.every((t, i) => t === FRAME_SANDBOX_TOKENS[i]);
  if (!ok) {
    throw new Error(
      `MessageFrame: refusing to render. sandbox is [${tokens.join(', ')}], ` +
        `must be exactly [${FRAME_SANDBOX_TOKENS.join(', ')}].`,
    );
  }
}

function clampHeight(px: number): number {
  if (!Number.isFinite(px) || px <= 0) return FALLBACK_FRAME_HEIGHT;
  return Math.min(MAX_FRAME_HEIGHT, Math.max(MIN_FRAME_HEIGHT, Math.ceil(px)));
}

/**
 * Frame documents whose stylesheets have been adopted. `load` can be seen
 * twice for one document (the listener and the direct call below), and a
 * second adoption would add the dark adaptation on top of itself.
 */
const styledDocs = new WeakSet<Document>();

/** No link is hovered, keyboard-focused or selected. One instance, so a reset re-renders nothing. */
const NO_PREVIEW: readonly URL[] = [];

/**
 * How often the preview is re-read, where the frame document runs no
 * listener, while the frame has focus but the pointer is elsewhere: keyboard
 * focus and keyboard selection. Under the pointer it is re-read every frame.
 */
const FOCUS_POLL_MS = 150;

/**
 * Whether a pointer event comes from something that hovers: a mouse or a pen,
 * on a device that has one. A finger does not, and a tap leaves `:hover` stuck
 * on whatever it touched. Some engines report a tap as a mouse (Playwright's
 * WebKit does), so the device's own `any-hover` is asked as well: a phone
 * answers no whatever the event claims. Where neither is known, yes.
 */
function isHoverPointer(event: Event): boolean {
  if ((event as PointerEvent).pointerType === 'touch') return false;
  if (typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(any-hover: hover)').matches;
}

/** `querySelector` that treats a selector the engine does not know as matching nothing. */
function queryFirst(doc: Document, selector: string): Element | null {
  try {
    return doc.querySelector(selector);
  } catch {
    return null;
  }
}

/**
 * Whether focus on `el` came from the keyboard. A link clicked with the mouse
 * is focused too, and must not keep its URL on screen after the pointer has
 * moved on. An engine that does not know `:focus-visible` says yes.
 */
function focusIsVisible(el: Element): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
}

/**
 * Chooses the renderer. With `isolation` the body is framed from the
 * user-content origin (ADR 0078); without it, or once this page has seen that
 * frame refused by its own CSP (or the server say it has no such origin), the
 * srcdoc renderer of ADR 0017/0052 draws it exactly as before.
 */
export function MessageFrame(props: MessageFrameProps): JSX.Element {
  const [legacy, setLegacy] = useState(false);
  const { isolation, bodyText: _bodyText, ...srcdocProps } = props;
  if (isolation && !legacy && !isLegacyForced()) {
    return <IsolatedMessageFrame {...props} isolation={isolation} onLegacy={() => setLegacy(true)} />;
  }
  return <SrcdocMessageFrame {...srcdocProps} />;
}

function SrcdocMessageFrame({
  html,
  css = '',
  appearance = 'light',
  frameKey,
  messageId = frameKey,
  onOpenLink,
  onMailto,
  onHeight,
  fetchSubresource = defaultSubresourceFetcher,
  onSubresources,
  fit = 'width',
  onFit,
}: Omit<MessageFrameProps, 'isolation' | 'bodyText'>): JSX.Element {
  const frameRef = useRef<HTMLIFrameElement>(null);
  // A ring when the keyboard walks into the message (frameFocus.ts).
  useFrameFocusRing(frameRef);
  // Reserved from an estimate of the markup, not the 48-px floor, so the
  // measured height corrects it instead of pushing the pane down (#170).
  const [height, setHeight] = useState<number>(() => estimateFrameHeight(html));
  const [refused, setRefused] = useState(false);
  const [linkPreview, setLinkPreview] = useState<readonly URL[]>(NO_PREVIEW);
  const [body, setBody] = useState<ResolvedBody>(() => initialBody(html, css));
  /*
   * Reset-on-prop-change, done during render rather than in the effect below.
   * React re-runs this component before committing anything, so the frame
   * never paints one message's body under another message's key — and the
   * fetch effect is left doing only what an effect is for.
   */
  const [bodySource, setBodySource] = useState({ frameKey, messageId, html, css });
  if (bodySource.frameKey !== frameKey || bodySource.html !== html || bodySource.css !== css) {
    setBodySource({ frameKey, messageId, html, css });
    const next = initialBody(html, css);
    // The same message re-rendered -- "Load images" is exactly that -- keeps
    // the document it is showing until the new one is resolved. Starting from
    // the empty body instead blanked the whole message, text and all, for as
    // long as the images took to fetch: seconds, on a cold proxy cache or a
    // phone connection. The frame effect keys on body.html, so the old
    // document stays put, listeners and scroll position included, until the
    // resolution lands. A different message still starts empty: one
    // message's body must never paint under another's key.
    setBody(
      next.pending && bodySource.messageId === messageId && !body.pending
        ? { ...body, pending: true }
        : next,
    );
    // Another message reserves its own estimate; the same one keeps the
    // height it has, which is already measured.
    if (bodySource.messageId !== messageId) setHeight(estimateFrameHeight(html));
  }

  // Kept in refs so the frame effect does not re-run — and therefore does not
  // rebuild the document and lose scroll position — when a caller passes a new
  // inline arrow function for either callback.
  const onOpenLinkRef = useRef(onOpenLink);
  const onMailtoRef = useRef(onMailto);
  const onHeightRef = useRef(onHeight);
  const onSubresourcesRef = useRef(onSubresources);
  const onFitRef = useRef(onFit);
  useEffect(() => {
    onOpenLinkRef.current = onOpenLink;
    onMailtoRef.current = onMailto;
    onHeightRef.current = onHeight;
    onSubresourcesRef.current = onSubresources;
    onFitRef.current = onFit;
  });

  /*
   * The fit mode is read by `measure()` through a ref, and a change asks the
   * live document to be measured again: rebuilding the frame for a zoom
   * change would refetch nothing but would still throw away its listeners
   * and flash an empty white card.
   */
  const fitRef = useRef(fit);
  const remeasureRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (fitRef.current === fit) return;
    fitRef.current = fit;
    remeasureRef.current?.();
  }, [fit]);

  /*
   * Fetch every inline part and sealed remote ref, then hand the rewritten
   * body to the frame effect below.
   *
   * The registry is created per run and revoked in the cleanup, so the object
   * URLs of the message being left go away the moment a new `html`/`frameKey`
   * arrives and on unmount — the two moments a reading pane leaks. A resolution
   * that lands after the cleanup revokes its own URLs immediately rather than
   * writing them into a document that is gone.
   */
  useEffect(() => {
    if (!hasSubresources(html, css)) return;

    const registry = new ObjectUrlRegistry();
    const controller = new AbortController();
    let cancelled = false;

    void resolveFrameSubresources(html, fetchSubresource, registry, controller.signal, { css }).then(
      (outcome) => {
        if (cancelled) {
          registry.revokeAll();
          return;
        }
        setBody({ ...outcome, pending: false });
      },
    );

    return () => {
      cancelled = true;
      controller.abort();
      registry.revokeAll();
    };
  }, [html, css, frameKey, fetchSubresource]);

  useEffect(() => {
    if (body.pending) return;
    onSubresourcesRef.current?.({
      requested: body.requested,
      failed: body.failed,
      refused: body.refused,
      svgBlocked: body.svgBlocked,
    });
  }, [body]);

  const applyHeight = useCallback((px: number) => {
    const clamped = clampHeight(px);
    setHeight(clamped);
    onHeightRef.current?.(clamped);
  }, []);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    try {
      assertSandboxIsExactlyTheGrant(frame);
    } catch (err) {
      setRefused(true);
      // Surfacing this is the point: a drifted sandbox is a build-level
      // mistake, and the tripwire test exists so it never reaches here.
      console.error(err);
      return;
    }
    setRefused(false);

    let frameId = 0;
    let disposed = false;
    let measured = false;
    const imgs: HTMLImageElement[] = [];

    /**
     * Fit-to-width (plan/15 §7.3), done entirely from this realm.
     *
     * A 600-px marketing table in a 358-px phone pane used to be clipped by
     * the reset's `overflow-x: hidden`: the right third of the newsletter was
     * simply not there. The parent reads the body's natural width, and if it
     * is wider than the frame writes CSS `zoom` on the frame's root — `zoom`
     * rather than `transform: scale` because it re-lays the document out, so
     * the measured height and hit-testing stay true. Below the floor the rest
     * scrolls sideways inside the frame, on the body, the root keeping its
     * `overflow-x: hidden`.
     *
     * Two style properties written through the CSSOM, and nothing else: no
     * markup, no script and no new sandbox token or CSP source is involved,
     * and the frame could not run anything to react to them if it wanted to.
     * Both are cleared first so every measurement is of the natural width,
     * which is what lets a late image that widens the body re-fit it.
     */
    const fitWidth = (doc: Document): number => {
      const root = doc.documentElement;
      const docBody = doc.body;
      if (!docBody) return 1;
      root.style.zoom = '';
      docBody.style.overflowX = '';
      const natural = Math.max(root.scrollWidth, docBody.scrollWidth);
      const result = fitFrame(natural, root.clientWidth, fitRef.current);
      if (result.zoom !== 1) root.style.zoom = String(result.zoom);
      if (result.scrollX) docBody.style.overflowX = 'auto';
      onFitRef.current?.({ ...result, wide: natural > root.clientWidth + 1 });
      return result.zoom;
    };

    /**
     * One measurement, now. The body's own height, not the root's: a root
     * never reports less than its viewport, so a frame once made tall (by the
     * fallback, say) could never shrink again -- frame.js measures the same
     * way for the isolated renderer.
     */
    const measureNow = () => {
      if (disposed) return;
      try {
        const doc = frame.contentDocument;
        // Mid-navigation: `srcdoc` was just replaced (loading remote images
        // does exactly that) and the new document has not parsed yet, or the
        // frame has no document at all yet. Its `load` re-measures, so wait
        // for it; a document that never arrives gets the timed fallback.
        // Measuring now would apply the fallback for a matter of timing, and
        // every message opened would jump 48 -> 600 -> its height.
        if (!doc?.documentElement || doc.readyState === 'loading') return;
        const docBody = doc.body;
        if (!docBody) return;
        noteFrameWidth(frame.clientWidth);
        const zoom = fitWidth(doc);
        measured = true;
        applyHeight(Math.max(docBody.scrollHeight, docBody.offsetHeight) * zoom);
      } catch {
        // Reading contentDocument can throw if the frame was torn down or
        // the origin turned out not to be reachable. A fixed height with the
        // frame's own scrollbar is the documented degraded mode.
        measured = true;
        applyHeight(FALLBACK_FRAME_HEIGHT);
      }
    };

    const measure = () => {
      if (disposed) return;
      if (frameId) return;
      frameId = requestAnimationFrame(() => {
        frameId = 0;
        measureNow();
      });
    };

    /*
     * A document that never yields a height in SILENT_FRAME_MS gets the
     * fallback, and the miss is reported: it is a renderer fault, not a slow
     * message. A later measurement still replaces it.
     */
    const silentTimer = window.setTimeout(() => {
      if (disposed || measured) return;
      applyHeight(FALLBACK_FRAME_HEIGHT);
      reportClientError({
        kind: 'error',
        message: `message frame: no height after ${SILENT_FRAME_MS} ms; using the fallback`,
      });
    }, SILENT_FRAME_MS);

    /*
     * Text set in a web font is laid out again when the font arrives, and a
     * print re-lays the document at the page's width. Both change the height
     * without changing the frame's width, which is all the observer below
     * watches. Printing does not wait for an animation frame, so that one is
     * measured at once.
     */
    const onBeforePrint = () => measureNow();
    window.addEventListener('beforeprint', onBeforePrint);
    let fontsDoc: Document | null = null;
    const onFontsLoaded = () => measure();

    /**
     * One delegated handler, created in this realm, for every way a link can
     * be activated. It calls `preventDefault` unconditionally and first: the
     * decision about whether anything opens is made afterwards, in the parent,
     * so an href shape nobody anticipated results in nothing happening rather
     * than in a default navigation. Cancelling the click also cancels the
     * `target="_blank"` the link was prepared with, so where this runs, the
     * confirm below is the only way a link opens.
     *
     * WebKit never calls it (ADR 0052); `prepareFrameLinks` is what makes a
     * link work there.
     */
    const onActivate = (event: Event) => {
      if (event.type === 'keydown') {
        const key = (event as KeyboardEvent).key;
        if (key !== 'Enter' && key !== ' ') return;
      }
      event.preventDefault();
      event.stopPropagation();

      // Duck-typed, NOT `target instanceof Element`: the frame is a separate
      // realm with its own `Element`, so a cross-realm `instanceof` is always
      // false and the handler would silently do nothing at all -- which looks
      // exactly like working code until a link is clicked.
      const target = event.target as { closest?: (sel: string) => Element | null } | null;
      if (!target || typeof target.closest !== 'function') return;
      const anchor = target.closest('a');
      if (!anchor) return;

      // Read the attribute, not `anchor.href`: the property is already
      // resolved against the frame's base URL, which would turn a relative
      // value into an absolute URL on our own origin.
      const href = anchor.getAttribute('href');
      // A mailto: opens this app's composer, prefilled -- nothing is sent, so
      // there is nothing to confirm. The caller parses the href (#343).
      if (href !== null && /^\s*mailto:/i.test(href)) {
        onMailtoRef.current?.(href.trim());
        return;
      }
      // A same-document link (ADR 0101): the pane scrolls to its target.
      // Nothing navigates; a fragment with no target in this message does
      // nothing.
      const fragment = fragmentTargetID(href);
      if (fragment !== null) {
        const doc = (anchor as Element).ownerDocument;
        const frame = frameRef.current;
        const target = doc.getElementById(fragment);
        if (frame && target) scrollFrameTo(frame, fragmentPosition(doc, target));
        return;
      }
      const url = openableURL(href);
      if (!url) return; // everything else that is not http(s) is inert

      // The full destination is shown before anything opens, with the reasons
      // it looks deceptive when it does (ADR 0101): the link's text is read
      // here, in the parent realm, as data and only for that comparison. A
      // native confirm on this renderer; the isolated one draws its own.
      const warnings = linkWarnings(url, linkTextOf(anchor.textContent));
      if (!window.confirm(confirmMessage(url, warnings))) return;
      (onOpenLinkRef.current ?? defaultOpenLink)(url);
    };

    /*
     * The URL preview (ADR 0053): while a link is hovered, keyboard-focused or
     * in the selection, the bar under the frame shows the URL it opens.
     *
     * Everything here is read out of the frame, never written into it: which
     * link is under the pointer, which has focus, what is selected. The URL is
     * always the one `frameLinkURL` re-validates from the `href`
     * `prepareFrameLinks` wrote, never the link's text.
     *
     * Two engines, one reading of the frame's state:
     *
     *  - Chromium runs the `pointerover`/`pointerout`/`focusin`/`focusout`/
     *    `selectionchange` listeners attached below, exactly as it runs the
     *    click handler. The first one that fires sets `listenersRun`, and from
     *    then on this document is served by its events alone.
     *  - WebKit runs none of them (ADR 0052), but a query from this realm
     *    still sees `:hover`, `:focus-visible` and the selection, because
     *    those are the engine's state and not script. So until a listener has
     *    been seen to run, the same state is polled: every animation frame
     *    while a mouse or pen is over the frame element (whose
     *    `pointerenter`/`pointerleave` fire in the parent on both engines),
     *    and every FOCUS_POLL_MS while the frame has focus without the
     *    pointer (Tab, keyboard selection). Never otherwise: a message being
     *    read with the pointer elsewhere costs nothing. One poll is two
     *    selector queries and a selection read, measured at well under 0.1 ms
     *    on a 20 000-element body in both engines.
     *
     * A touch is not a hover. A tap leaves `:hover` stuck on the link it
     * opened, so a touch pointer starts no poll and shows no bar; a long-press
     * keeps the engine's own URL preview or menu, which the bar, taking no
     * pointer events, never covers. A selection made by touch still shows it.
     */
    let pointerInside = false;
    let listenersRun = false;
    /** The link under the pointer as the frame's own events report it. */
    let eventHover: URL | null = null;
    let shownKey = '';
    let cancelPoll: (() => void) | null = null;
    let focusTimer = 0;

    const readPreview = (): readonly URL[] => {
      let doc: Document | null;
      try {
        doc = frame.contentDocument;
      } catch {
        return NO_PREVIEW;
      }
      if (!doc?.body) return NO_PREVIEW;

      const hovered = listenersRun
        ? eventHover
        : pointerInside
          ? frameLinkURL(queryFirst(doc, 'a:hover'))
          : null;
      if (hovered) return [hovered];

      // Focus and selection count only while the frame has focus: both
      // outlive a click elsewhere in the app, and so would the bar.
      let focused: boolean;
      try {
        focused = doc.hasFocus();
      } catch {
        focused = false;
      }
      if (!focused) return NO_PREVIEW;

      const active = doc.activeElement;
      const focusedURL = active && focusIsVisible(active) ? frameLinkURL(active) : null;
      if (focusedURL) return [focusedURL];
      return selectedLinkURLs(doc);
    };

    const refreshPreview = () => {
      if (disposed) return;
      const urls = readPreview();
      const key = urls.map((url) => url.href).join('\n');
      if (key === shownKey) return;
      shownKey = key;
      setLinkPreview(urls.length ? urls : NO_PREVIEW);
    };

    const stopPoll = () => {
      cancelPoll?.();
      cancelPoll = null;
    };
    const schedulePoll = () => {
      if (cancelPoll || disposed || listenersRun) return;
      if (pointerInside) {
        const id = requestAnimationFrame(pollTick);
        cancelPoll = () => cancelAnimationFrame(id);
      } else if (document.activeElement === frame) {
        const id = window.setTimeout(pollTick, FOCUS_POLL_MS);
        cancelPoll = () => window.clearTimeout(id);
      }
    };
    function pollTick() {
      cancelPoll = null;
      refreshPreview();
      schedulePoll();
    }

    /** Any of these running proves this engine runs them; the poll is then redundant. */
    const listenerRan = () => {
      listenersRun = true;
      stopPoll();
    };
    // Pointer events rather than mouse events: they say what the pointer
    // is, and the mouse events a tap is followed by for compatibility do not.
    const onFramePointerOver = (event: Event) => {
      listenerRan();
      eventHover = isHoverPointer(event) ? frameLinkURL(event.target) : null;
      refreshPreview();
    };
    const onFramePointerOut = (event: Event) => {
      listenerRan();
      // Out of the document altogether; moving to another element is a
      // pointerover of its own, which sets the next value.
      if ((event as PointerEvent).relatedTarget) return;
      eventHover = null;
      refreshPreview();
    };
    const onFrameFocusOrSelection = () => {
      listenerRan();
      // focusout fires before the next element is active.
      queueMicrotask(refreshPreview);
    };

    const onPointerEnter = (event: Event) => {
      if (!isHoverPointer(event)) return;
      pointerInside = true;
      refreshPreview();
      schedulePoll();
    };
    const onPointerLeave = () => {
      pointerInside = false;
      eventHover = null;
      stopPoll();
      refreshPreview();
      schedulePoll();
    };
    /** Focus moved into or out of the frame; the parent's own window sees that on every engine. */
    const onParentFocusChange = () => {
      window.clearTimeout(focusTimer);
      // `activeElement` settles after blur/focus has been dispatched.
      focusTimer = window.setTimeout(() => {
        if (disposed) return;
        stopPoll();
        refreshPreview();
        schedulePoll();
      }, 0);
    };

    const onLoad = () => {
      if (disposed) return;
      let doc: Document | null;
      try {
        doc = frame.contentDocument;
      } catch {
        applyHeight(FALLBACK_FRAME_HEIGHT);
        return;
      }
      // No document yet: its own `load` comes later, and one that never does
      // is what the timed fallback above is for.
      if (!doc) return;

      // Styles before anything is measured or shown (ADR 0081): the
      // sender's sheet, then -- when the root asks for it -- the dark
      // adaptation, which reads the styles the first one produced. Adopted,
      // not inlined: this document inherits the app shell's CSP, which
      // admits no <style> but the reset's.
      if (!styledDocs.has(doc) && doc.documentElement) {
        styledDocs.add(doc);
        try {
          adoptStylesheet(doc, body.css);
          applyDarkAdaptation(doc);
        } catch (err) {
          // Unstyled is the degraded mode; a thrown error here must not
          // take the reading pane down with it.
          console.error(err);
        }
      }
      frame.style.opacity = '';

      doc.addEventListener('click', onActivate, true);
      doc.addEventListener('auxclick', onActivate, true);
      doc.addEventListener('keydown', onActivate, true);
      doc.addEventListener('pointerover', onFramePointerOver, true);
      doc.addEventListener('pointerout', onFramePointerOut, true);
      doc.addEventListener('focusin', onFrameFocusOrSelection, true);
      doc.addEventListener('focusout', onFrameFocusOrSelection, true);
      doc.addEventListener('selectionchange', onFrameFocusOrSelection);
      eventHover = null;
      refreshPreview();

      // After the handler is in place, so where it runs a prepared link is
      // never without its confirm. Until this line, as before ADR 0052, a link
      // in a frame whose listeners never run does nothing.
      prepareFrameLinks(doc);

      // Late-loading inline images are the main cause of a stale height.
      for (const img of Array.from(doc.images)) {
        imgs.push(img);
        img.addEventListener('load', measure);
        img.addEventListener('error', measure);
      }
      // `fonts` is missing in jsdom and in some embedded engines.
      if (doc.fonts !== undefined) {
        fontsDoc = doc;
        doc.fonts.addEventListener('loadingdone', onFontsLoaded);
        void doc.fonts.ready.then(onFontsLoaded, () => undefined);
      }
      measure();
    };

    remeasureRef.current = measure;

    /*
     * A phone turned sideways, or a window dragged wider, changes what fits.
     * Only the width is watched: the height is ours, and reacting to it would
     * make every measurement schedule the next one.
     */
    let lastWidth = frame.clientWidth;
    const resizes =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => {
            if (frame.clientWidth === lastWidth) return;
            lastWidth = frame.clientWidth;
            measure();
          })
        : null;
    resizes?.observe(frame);

    frame.addEventListener('load', onLoad);
    frame.addEventListener('pointerenter', onPointerEnter);
    frame.addEventListener('pointerleave', onPointerLeave);
    window.addEventListener('blur', onParentFocusChange);
    window.addEventListener('focus', onParentFocusChange);
    // Hidden until onLoad has adopted the stylesheets, so the unstyled
    // document never flashes. Opacity, not visibility or display: the frame
    // keeps its box and its load is not deferred.
    frame.style.opacity = '0';
    frame.srcdoc = buildFrameDocument(body.html, appearance);

    // jsdom and some browsers fire `load` for a srcdoc frame before this
    // effect's listener is attached; measuring once directly covers that.
    if (frame.contentDocument?.readyState === 'complete') onLoad();

    return () => {
      disposed = true;
      window.clearTimeout(silentTimer);
      window.removeEventListener('beforeprint', onBeforePrint);
      fontsDoc?.fonts.removeEventListener('loadingdone', onFontsLoaded);
      if (remeasureRef.current === measure) remeasureRef.current = null;
      resizes?.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      frame.removeEventListener('load', onLoad);
      frame.removeEventListener('pointerenter', onPointerEnter);
      frame.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('blur', onParentFocusChange);
      window.removeEventListener('focus', onParentFocusChange);
      window.clearTimeout(focusTimer);
      stopPoll();
      // The next document starts with nothing previewed.
      setLinkPreview(NO_PREVIEW);
      for (const img of imgs) {
        img.removeEventListener('load', measure);
        img.removeEventListener('error', measure);
      }
      try {
        const doc = frame.contentDocument;
        doc?.removeEventListener('click', onActivate, true);
        doc?.removeEventListener('auxclick', onActivate, true);
        doc?.removeEventListener('keydown', onActivate, true);
        doc?.removeEventListener('pointerover', onFramePointerOver, true);
        doc?.removeEventListener('pointerout', onFramePointerOut, true);
        doc?.removeEventListener('focusin', onFrameFocusOrSelection, true);
        doc?.removeEventListener('focusout', onFrameFocusOrSelection, true);
        doc?.removeEventListener('selectionchange', onFrameFocusOrSelection);
      } catch {
        // Frame already gone; nothing to detach.
      }
    };
  }, [body.html, body.css, appearance, messageId, applyHeight]);

  if (refused) {
    return (
      <p role="status" className="message-frame-refused">
        This message could not be displayed safely.
      </p>
    );
  }

  return (
    <>
      <iframe
        // A new message throws the document away rather than reusing one that
        // has another message's listeners and scroll position on it. Keyed on
        // the message, not the render: a new render of the same message
        // replaces srcdoc when its body is ready (see the reset above).
        key={messageId}
        ref={frameRef}
        title="Message body"
        // Written as a literal so the tripwire test can grep this file for the
        // token that must never appear next to it.
        sandbox={FRAME_SANDBOX}
        referrerPolicy="no-referrer"
        scrolling="no"
        loading="eager"
        className="message-frame"
        style={{
          display: 'block',
          width: '100%',
          height: `${height}px`,
          border: 0,
          // The frame's own canvas (white, or the dark one), painted here too
          // so nothing flashes the other colour before the document loads.
          ...frameCanvas(appearance),
        }}
      />
      {linkPreview.length > 0 && <LinkPreview urls={linkPreview} />}
    </>
  );
}

export default MessageFrame;
